import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHero } from "@/components/Sections";
import { Calendar, RefreshCw, ExternalLink } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export default function Apod() {
  const [date, setDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previous, setPrevious] = useState([]);

  const load = async (d) => {
    setLoading(true);
    setError(null);
    try {
      const params = d ? { date: d } : {};
      const r = await api.get("/apod", { params });
      setData(r.data);
    } catch (e) {
      console.error("APOD load failed:", e);
      setError("Could not load today's astronomy image.");
    } finally {
      setLoading(false);
    }
  };

  const loadPrevious = async () => {
    try {
      const today = new Date();
      const dates = [];
      for (let i = 1; i <= 6; i++) {
        const dt = new Date(today);
        dt.setDate(today.getDate() - i);
        dates.push(dt.toISOString().slice(0, 10));
      }
      const responses = await Promise.all(dates.map((d) => api.get("/apod", { params: { date: d } }).then(r => r.data).catch(() => null)));
      setPrevious(responses.filter(Boolean));
    } catch (e) {
      console.error("APOD previous-days load failed:", e);
    }
  };

  // eslint-disable-next-line
  useEffect(() => { load(); loadPrevious(); }, []);

  return (
    <>
      <PageHero
        eyebrow="Astronomy Picture of the Day"
        title="The cosmos,"
        accent="every day."
        subtitle="A daily image of the universe with an explanation written by professional astronomers."
      >
        <div className="flex flex-wrap gap-3 items-center">
          <label className="btn-pill btn-secondary cursor-pointer" data-testid="apod-date-picker">
            <Calendar size={14} />
            <span className="text-white/70 text-xs uppercase tracking-widest">Date</span>
            <input
              type="date"
              value={date}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => { setDate(e.target.value); load(e.target.value); }}
              className="bg-transparent border-0 outline-none text-sm text-white"
            />
          </label>
          <button onClick={() => { setDate(""); load(); }} className="btn-pill btn-ghost" data-testid="apod-today-btn">
            <RefreshCw size={14} /> Today
          </button>
        </div>
      </PageHero>

      <section className="max-w-7xl mx-auto px-6 pb-20">
        {loading ? (
          <div className="glass p-6"><Skeleton className="h-[60vh] w-full rounded-2xl" /></div>
        ) : error ? (
          <div className="glass p-10 text-white/70">{error}</div>
        ) : data ? (
          <div className="glass p-5 md:p-8 fade-in-up" data-testid="apod-card">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-7">
                {data.media_type === "video" ? (
                  <div className="aspect-video rounded-2xl overflow-hidden">
                    <iframe
                      src={data.url}
                      title={data.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <img
                    src={data.hdurl || data.url}
                    alt={data.title}
                    className="w-full rounded-2xl"
                    loading="lazy"
                  />
                )}
              </div>
              <div className="lg:col-span-5 flex flex-col gap-5">
                <div className="eyebrow">{data.date}</div>
                <h2 className="display text-3xl md:text-4xl lg:text-5xl">{data.title}</h2>
                <p className="text-white/70 leading-relaxed text-[15px]">{data.explanation}</p>
                {data.copyright && (
                  <div className="text-xs text-white/45">© {data.copyright.trim()}</div>
                )}
                <a
                  href={data.hdurl || data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-pill btn-secondary self-start"
                >
                  Open original <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>
        ) : null}
      </section>

      {previous.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-24">
          <div className="eyebrow-row mb-6">
            <span className="eyebrow">Previous days</span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
            {previous.map((p) => (
              <button
                key={p.date}
                onClick={() => { setDate(p.date); load(p.date); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="glass hover-lift overflow-hidden text-left group"
                data-testid={`apod-prev-${p.date}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-white/5">
                  {p.media_type === "image" ? (
                    <img src={p.url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  ) : (
                    <img src={p.thumbnail_url || ""} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                  )}
                </div>
                <div className="p-5">
                  <div className="eyebrow">{p.date}</div>
                  <div className="mt-2 font-semibold tracking-tight">{p.title}</div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
